<template>
  <AiActionWrapper>
    <template v-slot:content>
      <div>
        <div v-if="!isLoading && !hasError">
          <p class="h6 blue--text text--lighten-1 mb-1">
            {{ $t('common.summary') }}
          </p>
          <p class="subtitle-2 font-bold blue--text text--lighten-1 mb-0">
            {{ revTypeName }}
          </p>
          <p class="subtitle-2 font-bold blue-grey--text text--lighten-2 mb-2">
            {{ rev.name }}
          </p>
          <p class="body-2 mb-0" v-html="formattedSummary" />
        </div>
        <div v-if="!isLoading && hasError" class="error-container px-3 py-2">
          <div class="d-flex align-center mb-1">
            <v-icon class="mr-1" size="16" color="red lighten-2"
              >mdi-alert-outline</v-icon
            >
            <p class="subtitle-2 font-bold red--text text--lighten-2 ma-0 pa-0">
              {{ $t('ai_features.summarize_error_title') }}
            </p>
          </div>

          <p class="caption red--text text--lighten-2 mb-1">
            {{ $t('ai_features.summarize_error_message') }}
          </p>
        </div>
        <div class="d-flex justify-center w-100">
          <div
            v-if="isLoading"
            class="d-flex flex-column w-100 justify-center align-center py-8 px-"
          >
            <p class="body-2 blue--text text--lighten-1 mb-1">
              {{ $t('ai_features.generating_summary') }}
            </p>

            <div class="w-100 px-12 py-0">
              <v-progress-linear
                rounded
                class="mt-3"
                color="blue lighten-1"
                height="10"
                model-value="60"
                indeterminate
              ></v-progress-linear>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <v-tooltip top :key="summaryCopied" v-if="!isLoading && !hasError">
        <template v-slot:activator="{ on }">
          <Button
            v-on="on"
            icon="mdi-content-copy"
            size="small"
            :text="$t('common.button.copy_to_clipboard')"
            @click="handleCopyToClipboard"
          />
        </template>
        {{
          summaryCopied
            ? $t('common.copied')
            : $t('ai_features.copy_summary_text')
        }}
      </v-tooltip>
    </template>
  </AiActionWrapper>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '../../api'
import getEntityId from '../../lib/getEntityId'
import mixin from '../../mixins/mixin.vue'

import AiActionWrapper from './AiActionWrapper.vue'
import Button from '../UI/Button.vue'

const revModelTypes = {
  improvementIdea: 'improvement-ideas',
  problemSolving: 'problem-solving',
  quickAction: 'quick-actions',
}

export default {
  name: 'AiSummary',

  props: {
    rev: Object,
    isVisible: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    AiActionWrapper,
    Button,
  },

  mixins: [mixin],

  data() {
    return {
      typingInterval: null,
      typingIndex: 0,
      typingTimeout: null,
      summary: '',
      summaryCopied: false,
      isLoading: false,
      summaryText: '',
      hasError: false,
    }
  },

  watch: {
    rev() {
      this.fetchSummary()
    },

    summaryText() {
      if (!this.summaryText || this.typingInterval || this.typingTimeout) return

      this.typingTimeout = setTimeout(() => {
        this.typingTimeout = null
        this.typingInterval = setInterval(this.typing, 45)
      }, 500)
    },
  },

  mounted() {
    this.fetchSummary()
  },

  computed: {
    ...mapGetters(['organization', 'organizationId']),

    revTypeName() {
      return (
        this.organization?.customNamings?.[this.revType]?.singular ||
        this.$t(`common.rev_type.${_.snakeCase(this.revType)}_one`)
      )
    },

    revType() {
      return this.rev?.entityType ?? this.rev?.type
    },

    revId() {
      return getEntityId(this.rev)
    },

    formattedSummary() {
      return this.summary.replace(/\n/g, '<br>')
    },
  },

  methods: {
    typing() {
      if (this.typingIndex < this.summaryText.length) {
        this.summary += this.summaryText.charAt(this.typingIndex)
        this.typingIndex++
      } else {
        clearInterval(this.typingInterval)
        this.typingInterval = null
      }
    },

    resetTypingAnimation() {
      clearInterval(this.typingInterval)
      clearTimeout(this.typingTimeout)
      this.typingInterval = null
      this.typingTimeout = null
    },

    async fetchSummary() {
      try {
        this.resetTypingAnimation()
        this.isLoading = true
        this.hasError = false
        this.summary = ''
        this.summaryText = ''
        this.summaryCopied = false
        this.typingIndex = 0

        const response = await api.ai.getSummary(
          this.organizationId,
          revModelTypes[this.revType],
          this.revId
        )
        this.isLoading = false

        if (!response.ok) {
          this.hasError = true
        }
        const decoder = new TextDecoder()

        await this.appendSummaryChunks(response?.body, decoder)
      } catch (err) {
        this.showError(err)
        this.isLoading = false
        this.hasError = true
      }
    },

    async appendSummaryChunks(body, decoder) {
      if (!body) return

      if (typeof body.getReader === 'function') {
        const reader = body.getReader()

        try {
          let isDone = false

          while (!isDone) {
            const { done, value } = await reader.read()

            isDone = done

            if (!done && value) {
              this.summaryText += decoder.decode(value, { stream: true })
            }
          }

          this.summaryText += decoder.decode()
        } finally {
          reader.releaseLock?.()
        }

        return
      }

      for await (const chunk of body) {
        this.summaryText += decoder.decode(chunk, { stream: true })
      }

      this.summaryText += decoder.decode()
    },

    handleCopyToClipboard() {
      navigator.clipboard
        .writeText(`${this.rev?.name}\n${this.summaryText}`)
        .then(() => {
          this.summaryCopied = true
        })
        .catch(err => {
          console.error(err)
        })
    },
  },

  beforeDestroy() {
    this.hasError = false
    this.resetTypingAnimation()
    this.summary = this.summaryText
    this.summaryCopied = false
  },
}
</script>

<style scoped lang="scss">
.error-container {
  border: 1px solid $color-red;
  background: rgba(248, 43, 96, 0.2);
  border-radius: 3px;
}
</style>
